import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { MaintenanceEventsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { format } from 'date-fns'
import { ResultBadge, StateBadge, TypeBadge } from './badge'
import { useTable } from '@/helpers/useTable'
import { MaintenanceEventFilters, MaintenanceEventOrder, MaintenanceEventResult, MaintenanceEventState, SortOrder } from '@/types'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'

interface Props extends TableProps {
  filters?: MaintenanceEventFilters
}

export const Table = ({ pageSize, filters = {}, onIndex = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<MaintenanceEventsQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.maintenanceEvents.pageInfo, onIndex, basePath: '/maintenance' })

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<MaintenanceEventFilters, MaintenanceEventOrder>
        {...props}
        defaultSort={{ dueAt: SortOrder.Desc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.maintenanceEvents.nodes = dedupe([
              ...previousData.maintenanceEvents.nodes,
              ...fetchMoreResult.maintenanceEvents.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 4,
            setValue: (set, value) => set(f => ({ ...f, stateEq: value as MaintenanceEventState })),
            type: 'select',
            options: Object.entries(MaintenanceEventState).map(([, value]) => ({
              value: value,
              display: (
                <StateBadge
                  maintenanceEvent={{
                    state: value,
                    maintenanceType: { name: '', color: 'red' },
                    result: MaintenanceEventResult.Passed
                  }}
                />
              ),
            })),
          },
        ]}
        headings={[
          { name: 'Type' },
          { name: 'Asset' },
          { name: 'Schedule' },
          { name: 'Due', sortField: 'dueAt', defaultDirection: SortOrder.Desc },
          { name: 'State' },
          { name: 'Result' },
        ]}
        rows={data.maintenanceEvents.nodes.map(maintenanceEvent => (
          {
            id: maintenanceEvent.id,
            columns: [
              <TypeBadge maintenanceEvent={maintenanceEvent} />,
              maintenanceEvent.asset.name,
              maintenanceEvent.maintenanceSchedule?.summary,
              maintenanceEvent.dueAt ? format(maintenanceEvent.dueAt, 'KK:mm dd/MM/yyyy') : '',
              <StateBadge maintenanceEvent={maintenanceEvent} />,
              <ResultBadge maintenanceEvent={maintenanceEvent} />
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <EditDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
    </>
  )
}
